import React, { useCallback, useEffect, useState } from "react";
import { Radar } from "lucide-react";
import { SaddleCloth } from "./SaddleCloth";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

const monoStyle = {
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
};

export const fmtMtp = (m) => {
  if (m == null) return "—";
  if (m <= 0) return "POST";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
};

const Tile = ({ h, moveClass }) => {
  // "Move" badge: ◂ shorter (odds dropped), ▸ longer (odds drifted),
  // · unchanged / unknown.
  return (
    <div
      className={`shrink-0 flex flex-col items-center gap-1 px-2.5 py-2 rounded-md border bg-[#0a0a0a] ${moveClass}`}
      style={{
        borderColor: "#292524",
        minWidth: 94,
        ...monoStyle,
      }}
      data-testid={`next-race-tile-${h.S}`}
    >
      <SaddleCloth n={h.S} />
      <div
        className="text-[11px] text-[#f5ecd6] font-bold truncate max-w-[88px] text-center"
        title={h.horse}
      >
        {h.horse || "—"}
      </div>
      <div
        className="text-[9px] text-stone-500 uppercase tracking-[0.14em] truncate max-w-[88px] text-center"
        title={h.jockey}
      >
        {h.jockey || "—"}
      </div>
      <div className="flex flex-col items-center gap-0.5 w-full border-t border-stone-800 pt-1">
        <div className="text-[9px] text-stone-500 uppercase tracking-[0.18em]">
          ML
        </div>
        <div className="text-[12px] font-bold text-[#fde047]">
          {h.ml || "—"}
        </div>
        <div className="text-[9px] text-stone-500 uppercase tracking-[0.18em] mt-0.5">
          Live
        </div>
        <div
          className={`text-[13px] font-bold ${
            h.current_odds && h.current_odds !== "--" ? "text-[#84cc16]" : "text-stone-600"
          }`}
        >
          {h.current_odds && h.current_odds !== "--" ? h.current_odds : "—"}
        </div>
      </div>
    </div>
  );
};

// Track badge + Post + MTP, shared with Race at a Glance.
export const RaceTimeInfo = ({ track, race, postTime, mtp }) => (
  <>
    <span
      className="inline-flex items-center gap-2 px-3 h-8 rounded border border-[#365314] bg-[#1a2e05] text-[#a3e635] text-[11px] uppercase tracking-[0.18em] font-bold"
      data-testid="glance-track-tile"
    >
      {track}
      <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded bg-[#0a0a0a] text-[#f5ecd6] border border-[#365314] text-[10px]">
        R{race ?? "?"}
      </span>
    </span>
    <span className="text-[11px] uppercase tracking-[0.18em] text-stone-400 flex items-center gap-3">
      {postTime && (
        <span data-testid="glance-post-time">
          <span className="text-stone-600">Post </span>
          <span className="text-[#f5ecd6]">{postTime}</span>
        </span>
      )}
      <span data-testid="glance-mtp">
        <span className="text-stone-600">MTP </span>
        <span className={mtp != null && mtp <= 2 ? "text-[#f87171] font-bold animate-pulse" : "text-[#84cc16] font-bold"}>
          {fmtMtp(mtp)}
        </span>
      </span>
    </span>
  </>
);

export const NextRaceLineup = ({ apiBase, activeTrack, onPickTrack, onInfo }) => {
  const [data, setData] = useState(null);
  const [prevOdds, setPrevOdds] = useState({}); // post → previous current_decimal
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchLineup = useCallback(async () => {
    if (!activeTrack?.slug) {
      setData(null);
      return;
    }
    try {
      const resp = await fetch(
        `${apiBase}/next-race-lineup?track=${encodeURIComponent(activeTrack.slug)}`
      );
      if (!resp.ok) return;
      const j = await resp.json();
      setData((cur) => {
        // Snapshot the previous live odds so we can draw drift arrows.
        if (cur?.horses) {
          const snap = {};
          for (const h of cur.horses) {
            if (h.current_decimal != null) snap[h.S] = h.current_decimal;
          }
          setPrevOdds(snap);
        }
        return j;
      });
      setLastRefresh(Date.now());
    } catch {
      /* silent — passive widget */
    }
  }, [apiBase, activeTrack?.slug]);

  // Clear stale snapshot when the active track is cleared.
  useEffect(() => {
    if (!activeTrack?.slug) {
      setData(null);
      setPrevOdds({});
      setLastRefresh(null);
    }
  }, [activeTrack?.slug]);

  // Fire immediately whenever the active slug flips.
  useEffect(() => {
    fetchLineup();
  }, [fetchLineup]);

  // 30s refresh, pauses when tab hidden.
  useAutoRefresh(fetchLineup, {
    enabled: !!activeTrack?.slug,
    intervalMs: 30000,
  });

  useEffect(() => {
    onInfo?.(
      data
        ? { track: data.track, race: data.race, postTime: data.post_time, mtp: data.mtp_minutes }
        : null
    );
  }, [data, onInfo]);

  if (!activeTrack?.slug) {
    return null;
  }

  const horses = data?.horses || [];
  const moveClassFor = (h) => {
    const prev = prevOdds[h.S];
    const now = h.current_decimal;
    if (prev == null || now == null) return "";
    // 10% move threshold on decimal odds (e.g. 4.0 → 3.6 = -10%).
    const rel = (now - prev) / prev;
    if (rel <= -0.1) return "ring-1 ring-[#84cc16]/60"; // tightening
    if (rel >= 0.1) return "ring-1 ring-[#f87171]/60"; // drifting
    return "";
  };

  return (
    <section
      className="max-w-6xl mx-auto px-6 sm:px-10 pt-6"
      data-testid="next-race-lineup"
    >
      <div
        className="rounded-lg border border-stone-800 bg-[#0e0e0e] overflow-hidden"
        style={monoStyle}
      >
        <div
          className="flex items-center flex-wrap gap-x-4 gap-y-2 px-5 py-3 border-b border-stone-800"
          style={{
            background:
              "linear-gradient(90deg, #141414 0%, #0e0e0e 100%)",
          }}
        >
          <div className="flex items-center gap-2 text-[#f5ecd6]">
            <Radar size={16} className="text-[#84cc16]" />
            <span className="text-sm uppercase tracking-[0.2em]">
              Live Lineup
            </span>
          </div>
          <button
            type="button"
            onClick={() => onPickTrack?.({ slug: data?.track_slug || activeTrack.slug, race: data?.race ?? activeTrack.race, name: data?.track || activeTrack.name })}
            className="group inline-flex items-center gap-2 px-3 h-8 rounded border border-[#365314] bg-[#1a2e05] hover:bg-[#84cc16] hover:text-[#0a0a0a] text-[#a3e635] text-[11px] uppercase tracking-[0.18em] font-bold transition"
            data-testid="next-race-load-btn"
            title="Load this race into the DD Matrix (fires full One-Click Ticket)"
          >
            {data?.track || activeTrack.name}
            <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded bg-[#0a0a0a] text-[#f5ecd6] border border-[#365314] text-[10px]">
              R{data?.race ?? activeTrack.race ?? "?"}
            </span>
          </button>
          <div className="text-[11px] uppercase tracking-[0.18em] text-stone-400 flex items-center gap-3">
            {data?.post_time && (
              <span>
                <span className="text-stone-600">Post </span>
                <span className="text-[#f5ecd6]">{data.post_time}</span>
              </span>
            )}
            <span>
              <span className="text-stone-600">MTP </span>
              <span
                className={
                  (data?.mtp_minutes ?? activeTrack.mtp_minutes) != null &&
                  (data?.mtp_minutes ?? activeTrack.mtp_minutes) <= 2
                    ? "text-[#f87171] font-bold animate-pulse"
                    : "text-[#84cc16] font-bold"
                }
              >
                {fmtMtp(data?.mtp_minutes ?? activeTrack.mtp_minutes)}
              </span>
            </span>
            {lastRefresh && (
              <span className="text-stone-600 hidden md:inline">
                upd{" "}
                {new Date(lastRefresh).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            )}
          </div>
        </div>

        {data?.note && horses.length > 0 && (
          <div
            className="px-4 py-2 text-[11px] text-[#fde047] border-b border-stone-800 bg-[#1a1500]/40 flex items-start gap-2"
            style={monoStyle}
            data-testid="next-race-lineup-note"
          >
            <span className="shrink-0 mt-[1px]">⚠</span>
            <span>{data.note}</span>
          </div>
        )}

        <div className="px-4 py-3 overflow-x-auto">
          {horses.length === 0 ? (
            <div
              className="text-[11px] text-stone-500 italic px-2 py-6 text-center"
              data-testid="next-race-lineup-pending"
            >
              {data?.note ||
                "Lineup not published yet — try again closer to post."}
            </div>
          ) : (
            <div className="flex items-stretch gap-2 min-w-max">
              {horses.map((h) => (
                <Tile key={h.S} h={h} moveClass={moveClassFor(h)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
